import { Allow, parse } from "partial-json"
import { z } from "zod"
import {
  CollectInputFieldSchema,
  CollectInputSubmitSchema,
  type CollectInputField,
  type CollectInputSubmit,
} from "@common/schemas/icertis"

const SEND_TO_AGENT: CollectInputSubmit = { kind: "send_to_agent" }

const CollectInputPreviewShapeSchema = z.object({
  title: z.string().nullish(),
  reason: z.string().nullish(),
  fields: z.array(z.unknown()).optional(),
  submit: z.unknown().optional(),
})

const FieldTypingInItsValueSchema = z.object({
  type: z.enum(["text", "long_text", "number", "date", "currency"]),
  value: z.string(),
})

export interface CollectInputPreview {
  title: string | null
  reason: string | null
  fields: CollectInputField[]
  submit: CollectInputSubmit
}

function wholeOrValueStreamingCandidates(candidates: unknown[]): unknown[] {
  if (FieldTypingInItsValueSchema.safeParse(candidates.at(-1)).success) return candidates
  return candidates.slice(0, -1)
}

function validFields(candidates: unknown[]): CollectInputField[] {
  const seenIds = new Set<string>()
  const fields: CollectInputField[] = []
  for (const candidate of candidates) {
    const parsed = CollectInputFieldSchema.safeParse(candidate)
    if (!parsed.success || seenIds.has(parsed.data.id)) continue
    seenIds.add(parsed.data.id)
    fields.push(parsed.data)
  }
  return fields
}

export function parseCollectInputPreview(partialArgsJson: string): CollectInputPreview | null {
  let raw: unknown
  try {
    raw = parse(partialArgsJson, Allow.ALL)
  } catch {
    return null
  }
  const shape = CollectInputPreviewShapeSchema.safeParse(raw)
  if (!shape.success) return null
  const submit = CollectInputSubmitSchema.safeParse(shape.data.submit)
  return {
    title: shape.data.title ?? null,
    reason: shape.data.reason ?? null,
    fields: validFields(wholeOrValueStreamingCandidates(shape.data.fields ?? [])),
    submit: submit.success ? submit.data : SEND_TO_AGENT,
  }
}
